import { PrescriptionStatus } from '@services/dtos'
import { useSelector } from '@state/store'
import { PrescriptionStatusLabels } from '@utils/constants'
import { Autocomplete, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material'
import { GridToolbarContainer } from '@mui/x-data-grid-pro'
import { setPrescriptionFilters } from '@state/reducers/prescriptionReducer'
import { useEffect, useState } from 'react'
import moment from 'moment'
import DateFilterComponent, { DateRange } from '@components/DateFilterComponent'
import { useAppDispatch } from '@hooks/reduxHooks'
import { findAll as findAllExam } from '@state/thunks/procedureThunk'
import { Actions } from '@state/actions'
import { ModalityEnum, PatientDto } from '@services/api'
import PatientSearchComponent from '@components/patient/PatientSearchComponent'

const STATUS_OPTIONS =
  (Object.entries(PrescriptionStatusLabels) as Array<[PrescriptionStatus, string]>)
    .map(([value, label]) => ({ label, value }))
const MODALITY_OPTIONS = [ModalityEnum.PT, ModalityEnum.NM].map((modality) => ({ label: modality, value: modality }))

const PrescriptionsTableToolbar = () => {
  const dispatch = useAppDispatch()
  const { filters, exams, loadingExams } = useSelector(({ prescription, procedure, loading }) => ({
    filters: prescription.filters,
    exams: procedure.exams.datas,
    loadingExams: loading[Actions.PROCEDURE_FIND_ALL],
  }))
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>({
    startDate: moment(filters.from).toDate(),
    endDate: moment(filters.to).toDate(),
  })

  useEffect(() => {
    dispatch(findAllExam({ isNuclear: true }))
  }, [])

  const handleDateRangeChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange)
    dispatch(setPrescriptionFilters({
      from: moment(dateRange.startDate).format('YYYY-MM-DD'),
      to: moment(dateRange.endDate).format('YYYY-MM-DD'),
    }))
  }

  return (<GridToolbarContainer className="m-2 mb-0">
    <div className="flex justify-between items-center w-full flex-wrap">
      <div className="flex items-center space-x-2 flex-wrap">
        <div className="w-[300px]">
          <PatientSearchComponent
            isPatientExists
            autoCompleteSize='small'
            disabled={!!filters.patientId}
            onPatientChange={(selectedPatient?: PatientDto) => {
              dispatch(setPrescriptionFilters({ patientId: selectedPatient?.id }))
            }}
          />
        </div>
        <FormControl size="small">
          <InputLabel>Statut</InputLabel>
          <Select
            value={filters.status}
            onChange={(event: SelectChangeEvent) => {
              const value = event.target.value === '' ? undefined : (event.target.value as PrescriptionStatus)
              dispatch(setPrescriptionFilters({ status: value }))
            }}
            style={{
              minWidth: '200px',
              marginRight: 6,
            }}
            size="small"
            label="Statut"
          >
            <MenuItem value="">
              <em>Tous</em>
            </MenuItem>
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          size="small"
          options={exams}
          loading={loadingExams}
          getOptionLabel={(option) => option.label}
          value={exams.filter((exam) => (filters.examIds ?? []).includes(exam.id))}
          onChange={(_, selected) => {
            dispatch(setPrescriptionFilters({
              examIds: selected.map((exam) => exam.id),
            }))
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderTags={(selected, getTagProps) =>
            selected.map((exam, index) => (
              <Chip
                {...getTagProps({ index })}
                key={exam.id}
                label={exam.label}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Examens"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingExams && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          style={{ minWidth: '200px', marginRight: 6 }}
        />
        <FormControl size="small">
          <InputLabel>Modalité</InputLabel>
          <Select
            value={filters.modality}
            onChange={(event: SelectChangeEvent) => {
              const value = event.target.value === '' ? undefined : (event.target.value as ModalityEnum)
              dispatch(setPrescriptionFilters({ modality: value }))
            }}
            style={{
              minWidth: '200px',
              marginRight: 6,
            }}
            size="small"
            label="Statut"
          >
            <MenuItem value="">
              <em>Toutes</em>
            </MenuItem>
            {MODALITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormGroup>
          <FormControlLabel 
            control={<Checkbox 
            checked={filters.cancelledAppointment} 
            onChange={(event) => dispatch(setPrescriptionFilters({ cancelledAppointment: event.target.checked }))} />} 
            label="Rdv annulé uniquement" />
        </FormGroup>
        <DateFilterComponent
          dateRange={selectedDateRange}
          onDateRangeChange={handleDateRangeChange} />
      </div>
    </div>
  </GridToolbarContainer>)
}

export default PrescriptionsTableToolbar
